import { george, i18n, pr, theme } from "aaa";
import { ActionButton } from "components/common/georgeComponents";
import { ui } from "components/util/uiHelpers";
import { FormattedHTMLMessage } from "i18n/util";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { amountToCurrencyString } from "types/amount";
import { HouseholdInsuranceBenefitsInfo } from "./components/HoseholdInsuranceBenefitsInfo";
import { HouseholdInsuranceAccordion } from "./components/HouseholdInsuranceAccordion";
import { HouseholdInsuranceColumnContent } from "./components/HouseholdInsuranceColumnContent";
import { HouseholdInsuranceDenialInfo } from "./components/HouseholdInsuranceDenialInfo";
import { HouseholdInsuranceTwoColumnContainer } from "./components/HouseholdInsuranceTwoColumnContainer";
import { HouseholdInsuranceRoutes } from "./HouseholdInsuranceRoutes";
import { HouseholdInsuranceStore } from "./HouseholdInsuranceStore";
import trackingStore from "stores/TrackingStore";

const MAX_WIDTH = 760;

type FederalStateOption = {
    value: string;
    label: string;
}

const getFilterAttributeMap = (): FederalStateOption[] => ([
    {
        value: "1",
        label: ui.__("common.federalState.burgenland"),
    },
    {
        value: "2",
        label: ui.__("common.federalState.carinthia"),
    },
    {
        value: "3",
        label: ui.__("common.federalState.lowerAustria"),
    },
    {
        value: "4",
        label: ui.__("common.federalState.upperAustria"),
    },
    {
        value: "5",
        label: ui.__("common.federalState.salzburg"),
    },
    {
        value: "6",
        label: ui.__("common.federalState.styria"),
    },
    {
        value: "7",
        label: ui.__("common.federalState.tyrol"),
    },
    {
        value: "8",
        label: ui.__("common.federalState.vorarlberg"),
    },
    {
        value: "9",
        label: ui.__("common.federalState.vienna"),
    },
]);

const NextStepSection = () => (
    <pr.VFlex
        style={{
            borderRadius: 8,
            gap: 8,
            padding: 25,
        }}
    >
        <pr.P_24_TITLE
            style={{
                fontWeight: 600,
            }}
        >
            {ui.__("householdInsurance.landingPage.nextStep.title")}
        </pr.P_24_TITLE>
        <pr.P_16_CONTENT>
            {ui.__("householdInsurance.landingPage.nextStep.description")}
        </pr.P_16_CONTENT>
    </pr.VFlex>
)

type HouseholdInsuranceLandingPageProps = RouteComponentProps<{ id: string }> & i18n.InjectedIntlProps & {
    store: HouseholdInsuranceStore;
};

export const HouseholdInsuranceLandingPage = observer(({ store }: HouseholdInsuranceLandingPageProps) => {
    const federStateOptions = getFilterAttributeMap().sort((a, b) => a.label.localeCompare(b.label));
    const [selectedFederalState, setSelectedFederalState] = React.useState<FederalStateOption>(federStateOptions[0]);
    const [apartmentSize, setApartmentSize] = React.useState<string>("");
    const [apartmentSizeError, setApartmentSizeError] = React.useState<string | undefined>(undefined);
    const [isInfoExpanded, setIsInfoExpanded] = React.useState(false);
    const [isDenialInfoExpanded, setIsDenialInfoExpanded] = React.useState(false);

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;

    const handleInfoExpandClick = () => {
        setIsInfoExpanded(!isInfoExpanded);
    }

    const handleDenialInfoExpandClick = () => {
        setIsDenialInfoExpanded(!isDenialInfoExpanded);
    };

    const handleChangeApartmentSize = (_event: any, value: string) => {
        setApartmentSize(value);
        const error = validateApartmentSize(value);
        setApartmentSizeError(error);

        if (error) {
            console.log("handleChangeApartmentSize", error);
            store.calculationFee = undefined;
        }
    }

    const handleChangeFederalState = (_event: any, _index: number, value: string) => {
        console.log(value);
        const federalState = federStateOptions.find(option => option.value === value);
        if (federalState) {
            setSelectedFederalState(federalState);
        }
    }

    const handleClickNext = async () => {
        await trackingStore.trackEvent({ eventName: "orderNow" });
        pushRoute(HouseholdInsuranceRoutes.PERSONAL_DATA);
    }


    const validateApartmentSize = (value: string): string | undefined => {
        if (!value || value.length === 0) {
            return ui.__("householdInsurance.landingPage.calculation.apartmentSize.validation.required");
        }

        if (!/^\d+$/.test(value)) {
            return ui.__("householdInsurance.landingPage.calculation.apartmentSize.validation.integerOnly");
        }

        const numValue = parseInt(value, 10);


        if (numValue < 10) {
            return ui.__("householdInsurance.landingPage.calculation.apartmentSize.validation.min");
        }

        if (numValue > 999) {
            return ui.__("householdInsurance.landingPage.calculation.apartmentSize.validation.max");
        }

        return undefined;
    };

    const debouncedApiCall = React.useMemo(
        () => debounce(async (federalStateValue: string, apartmentSizeNum: number) => {
            try {
                const region = parseInt(federalStateValue);
                await store.calculateHouseholdInsuranceFee(region, apartmentSizeNum);
            } catch (error) {
                //ignore should never happen
            }
        }, 500),
        [apartmentSizeError]
    );

    React.useEffect(() => {
        return () => {
            debouncedApiCall.cancel();
        };
    }, [debouncedApiCall]);

    React.useEffect(() => {
        const apartmentSizeNum = parseInt(apartmentSize, 10);

        if (apartmentSize.length >= 2 && apartmentSizeNum >= 10 && apartmentSizeNum <= 999 && !apartmentSizeError) {
            debouncedApiCall(selectedFederalState.value, apartmentSizeNum);
        } else {
            debouncedApiCall.cancel();
        }
    }, [apartmentSize, selectedFederalState.value, apartmentSizeError, debouncedApiCall]);

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;
    const isNarrowMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 480;

    const getTitleFontStyle = () => {
        if (isNarrowMobile) {
            return { fontSize: 28, lineHeight: "34px" };
        } else if (isMobile) {
            return { fontSize: 34, lineHeight: "40px" };
        } else {
            return { fontSize: 42, lineHeight: "60px" };
        }
    };

    return (
        <BackgroundComponent style={{ boxSizing: "border-box", paddingTop: 48, paddingBottom: theme.MARGIN }}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 40,
                    marginTop: theme.MARGIN,
                    marginBottom: theme.MARGIN,
                }}
            >
                <div
                    style={{
                        lineHeight: "29px",
                        maxWidth: MAX_WIDTH,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${require("../../assets/householdInsurance_header_image.png")}) no-repeat`,
                            backgroundPosition: isMobile ? "75% 50%" : "40% 50%",
                            backgroundSize: "cover",
                            height: "fit-content",
                        }}
                    >
                        <div
                            style={{
                                padding: theme.MARGIN,
                            }}
                        >
                            <pr.P_18_CONTENT
                                style={{
                                    color: "white",
                                    marginBottom: 3,
                                }}
                            >
                                {ui.__("householdInsurance.landingPage.header.overline")}
                            </pr.P_18_CONTENT>
                            <div
                                style={{
                                    maxWidth: "fit-content",
                                }}
                            >
                                <pr.P_32_TITLE
                                    style={{
                                        color: "white",
                                        width: "fit-content",
                                        ...getTitleFontStyle(),
                                    }}
                                >
                                    {ui.__("householdInsurance.landingPage.header.title")}
                                </pr.P_32_TITLE>
                                <div
                                    style={{
                                        borderBottom: "2px solid white",
                                        margin: "16px 0",
                                        marginLeft: "0px",
                                        width: isMobile ? "100%" : "calc(100% + 20px)",
                                    }}
                                />
                            </div>
                            <pr.P_18_CONTENT
                                style={{
                                    color: "white",
                                }}
                            >
                                <FormattedHTMLMessage
                                    id="householdInsurance.landingPage.header.description"
                                />
                            </pr.P_18_CONTENT>
                        </div>
                    </div>

                </div>
                <div
                    style={{
                        padding: "0 20px",
                        textAlign: "center",
                    }}
                >
                    <pr.P_24_TITLE
                        style={{
                            fontWeight: 600,
                            marginBottom: 16,
                        }}
                    >
                        {ui.__("householdInsurance.landingPage.intro.title")}
                    </pr.P_24_TITLE>
                    <pr.P_16_CONTENT>
                        <FormattedHTMLMessage id="householdInsurance.landingPage.intro.text" />
                    </pr.P_16_CONTENT>
                </div>
                <george.Card
                    disableTopSpacing
                    style={{
                        borderRadius: 8,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)",
                    }}
                >
                    <div
                        style={{
                            alignItems: "center",
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            backgroundColor: "#1F4865",
                            display: "flex",
                            flexDirection: "row",
                            gap: 25,
                            padding: 25,
                        }}
                    >
                        <pr.IconUmbrella
                            style={{
                                width: 64,
                                height: 64,
                                color: "white",
                                display: "block"
                            }}
                        />
                        <pr.P_20_TITLE
                            style={{
                                color: "white",
                            }}
                        >
                            {ui.__("householdInsurance.landingPage.calculation.title")}
                        </pr.P_20_TITLE>
                    </div>
                    <HouseholdInsuranceTwoColumnContainer
                        style={{
                            ...(isMobile ? { paddingBottom: 40 } : { paddingBottom: 8 })
                        }}
                    >
                        <HouseholdInsuranceColumnContent
                            style={{
                                height: 124
                            }}
                        >
                            <pr.P_16_CONTENT
                                style={{
                                    fontWeight: 600,
                                    wordBreak: "break-all",
                                    marginBottom: 10,
                                }}
                            >
                                {ui.__("householdInsurance.landingPage.calculation.federalState.label")}
                            </pr.P_16_CONTENT>
                            <george.DropDown
                                items={federStateOptions}
                                style={{
                                    paddingBottom: 0,
                                    paddingTop: 0,
                                }}
                                value={selectedFederalState.value}
                                onChange={handleChangeFederalState}
                            />
                        </HouseholdInsuranceColumnContent>
                        <HouseholdInsuranceColumnContent
                            style={{
                                height: 124
                            }}
                        >
                            <pr.P_16_CONTENT
                                style={{
                                    fontWeight: 600,
                                    marginBottom: 10,
                                    wordBreak: "break-all",
                                }}
                            >
                                {ui.__("householdInsurance.landingPage.calculation.apartmentSize.label")}
                            </pr.P_16_CONTENT>
                            <george.TextField
                                aria-label={ui.__("householdInsurance.landingPage.calculation.apartmentSize.label")}
                                decorator="m²"
                                errorMessage={apartmentSizeError}
                                value={apartmentSize}
                                onChange={handleChangeApartmentSize}
                            />
                        </HouseholdInsuranceColumnContent>
                    </HouseholdInsuranceTwoColumnContainer>
                    <HouseholdInsuranceTwoColumnContainer
                        style={{
                            backgroundColor: "#135EE2",
                            justifyContent: "space-between",

                        }}
                    >
                        {(externalInterfaceStore.isGeorgeApp || ui.windowWidth < 443) ? (
                            !store.isCalculating && <pr.P_20_CONTENT
                                style={{
                                    color: "white",
                                }}
                            >
                                {ui.__("householdInsurance.landingPage.calculation.preliminaryBasePremium.label")}{" "}
                                {store.calculationFee ?
                                    <FormattedHTMLMessage
                                        id="householdInsurance.landingPage.calculation.preliminaryBasePremium.value"
                                        values={{ fee: amountToCurrencyString(store.calculationFee) }}
                                    /> : ui.__("householdInsurance.landingPage.calculation.preliminaryBasePremium.notAvailable")}
                            </pr.P_20_CONTENT>
                        ) : (
                            <>
                                <pr.P_20_CONTENT
                                    style={{
                                        color: "white",
                                    }}
                                >
                                    {ui.__("householdInsurance.landingPage.calculation.preliminaryBasePremium.label")}
                                </pr.P_20_CONTENT>
                                {
                                    !store.isCalculating && <pr.P_20_CONTENT
                                        style={{
                                            color: "white",
                                        }}
                                    >
                                        {store.calculationFee ?
                                            <FormattedHTMLMessage
                                                id="householdInsurance.landingPage.calculation.preliminaryBasePremium.value"
                                                values={{ fee: amountToCurrencyString(store.calculationFee) }}
                                            /> : ui.__("householdInsurance.landingPage.calculation.preliminaryBasePremium.notAvailable")}
                                    </pr.P_20_CONTENT>
                                }
                            </>
                        )}
                        <george.LoadingIndicator height="100%" isLoading={store.isCalculating} white />
                    </HouseholdInsuranceTwoColumnContainer>
                    <HouseholdInsuranceAccordion
                        expanded={isInfoExpanded}
                        label={ui.__("householdInsurance.landingPage.benefitsInfo.title")}
                        onToggle={handleInfoExpandClick}
                    >
                        <HouseholdInsuranceBenefitsInfo />
                    </HouseholdInsuranceAccordion>
                    <HouseholdInsuranceAccordion
                        expanded={isDenialInfoExpanded}
                        label={ui.__("householdInsurance.landingPage.denialInfo.title")}
                        onToggle={handleDenialInfoExpandClick}
                    >
                        <HouseholdInsuranceDenialInfo />
                    </HouseholdInsuranceAccordion>
                </george.Card>
                <pr.HCenterDiv>
                    <pr.P_16_CONTENT>
                        {ui.__("householdInsurance.landingPage.asteriskNote")}
                    </pr.P_16_CONTENT>
                </pr.HCenterDiv>
                {/* Commented out for now, due to mail from Christoph Freh 28.11.2025, might be reused again in the future */}
                {/* {isMobile ? (
                    <george.Card
                        disableTopSpacing
                        style={{
                            borderRadius: 8,
                            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)",
                        }}
                    >
                        <NextStepSection />
                    </george.Card>
                ) : (
                    <george.Card
                        disableTopSpacing
                        style={{
                            alignItems: "center",
                            borderRadius: 8,
                            borderBottomLeftRadius: 100,
                            borderTopLeftRadius: 100,
                            display: "flex",
                            gap: 32,
                        }}
                    >
                        <pr.HFlexCentered
                            style={{
                                backgroundColor: "#721C7A",
                                borderRadius: 100,
                                minHeight: 168,
                                minWidth: 168,
                            }}
                        >
                            <pr.IconUmbrellaWithPlusXL style={{ width: 96, height: 96 }} />
                        </pr.HFlexCentered>
                        <NextStepSection />
                    </george.Card>
                )} */}
                <pr.LightSeparatorLine />
                {/* Commented out for now, due to mail from Christoph Freh 28.11.2025, might be reused again in the future */}
                {/* <pr.HFlex
                    style={{
                        alignItems: "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 16,
                        justifyContent: "space-between",
                        margin: "0 auto",
                        maxWidth: MAX_WIDTH,
                        width: "95%",
                    }}
                > */}
                {/* <pr.P_18_CONTENT>
                        {
                            store.calculationFee && !store.isCalculating &&
                            <FormattedHTMLMessage
                                id="householdInsurance.landingPage.button.info"
                                values={{ fee: amountToCurrencyString(store.calculationFee) }}
                            />
                        }
                    </pr.P_18_CONTENT> */}
                <ActionButton
                    primary
                    messageId="householdInsurance.landingPage.button.label"
                    onClick={handleClickNext}
                    {...george.getActionButtonWebVersionProps()}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                />
                {/* </pr.HFlex> */}
            </div>
        </BackgroundComponent >
    );
});



// WEBPACK FOOTER //
// ./src/components/householdInsurance/HouseholdInsuranceLandingPage.tsx